import Vue from "vue"
import axios from 'axios'
import Notify from 'quasar/src/plugins/Notify.js';
import { camelCase } from 'lodash'
import router from '@/router'
import { preventRedirects } from '@/plugins/urlPaths'

/*
 * For each async mutation, dynamically add the three mutations:
 * SUCCESS - write the response to the store using the `stateKey` property
 * PENDING - set the `loadingKey` property in the store to true
 * FAILURE - Set `loadingKey` to false, and `errorCode` - for example 401, 404...
 *
 * stateKey, errorCode, errorMessage and loadingKey are prepended with the name of the action,
 * for example an action getData would lead to:
 *
 * mutations:
 *   GET_DATA_SUCCESS,
 *   GET_DATA_PENDING,
 *   GET_DATA_FAILURE
 *
 * state:
 *   getDataLoadingKey,
 *   getDataStateKey,
 *   getDataErrorCode
 *   getDataErrorMessage
 *
 * Note we use Vue.set, simply doing state[loadingKey] will not work, because the property needs
 * to be present initially for it to be included in the Vue reactivity system. Vue.set allows us
 * to add a reactive property dynamically.
 */

export const setMutationStates = (mutations, types) => {
    if (!types) return
    Object.keys(types).forEach(type => {
        mutations[types[type].BASE] = (state, payload) => {
            switch (payload.type) {
                case types[type].PENDING:
                    return Vue.set(state, types[type].loadingKey, payload.value)

                case types[type].SUCCESS:
                    Vue.set(state, types[type].statusCode, payload.statusCode)
                    return Vue.set(state, types[type].stateKey, payload.data)

                case types[type].FAILURE:
                    Vue.set(state, types[type].error, payload.error)
                    return Vue.set(state, types[type].statusCode, payload.statusCode)
            }
        }
    })
}

/*
 * createAsyncMutation('GET_INFO') will lead to:
 *
 * GET_INFO = {
 *      BASE: GET_INFO_BASE,
 *      SUCCESS: GET_INFO_SUCCESS,
 *      PENDING: GET_INFO_PENDING,
 *      FAILURE: GET_INFO_FAILURE,
 *      loadingKey: getInfoPending,
 *      statusCode: getInfoStatusCode,
 *      stateKey: getInfoData
 * }
 *
 */
export const createAsyncMutation = (type) => ({
    BASE: `${type}`,
    SUCCESS: `${type}_SUCCESS`,
    FAILURE: `${type}_FAILURE`,
    PENDING: `${type}_PENDING`,
    loadingKey: `${camelCase(type)}Pending`,
    statusCode: `${camelCase(type)}StatusCode`,
    stateKey: `${camelCase(type)}Data`
})

function getAuthHeader() {
    const token = JSON.parse(localStorage.getItem('token'))
    return {
        'Authorization': 'Bearer ' + token?.access_token
    }
}

function processError(store, mutationTypes, error) {
    console.log(error)
    setPendingFlag(store, mutationTypes, false)
    let code = 404
    let errorMessage = error?.message
    let errorMsgs = null

    if (error?.response) {
        code = error?.response?.status ?? 404

        console.log(error?.response?.data)

        if (error?.response?.data?.modelState && error?.response?.data?.modelState[""]) {
            errorMsgs = error.response.data.modelState[""]
        }

        if (error?.response?.data?.message && error?.response?.data?.message != 'An error has occurred.')
            errorMessage = error.response.data.message
        else if (error?.response?.data?.exceptionMessage)
            errorMessage = error.response.data.exceptionMessage
    }

    store.commit(mutationTypes.BASE, { type: mutationTypes.FAILURE, error: error, statusCode: code })

    console.log(errorMsgs)
    if (errorMsgs) {
        errorMessage = ''
        errorMsgs.forEach(err => {
            errorMessage += err + ' '
            if (!preventRedirects.includes(router.currentRoute.path)) {
                Notify.create({
                    badgeClass: 'hidden',
                    type: 'negative',
                    message: err
                })
            }
        })
    }
    else {
        if (!preventRedirects.includes(router.currentRoute.path)) {
            Notify.create({
                badgeClass: 'hidden',
                type: 'negative',
                message: errorMessage
            })
        }
    }

    return errorMessage
}

function processResponse(store, mutationTypes, response, callback) {
    let data = null
    if (callback) data = callback(response)
    else data = response?.data ?? null

    store.commit(mutationTypes.BASE, { type: mutationTypes.SUCCESS, data, statusCode: response?.status ?? '' })
    setPendingFlag(store, mutationTypes, false)

    return data
}

function reset(store, mutationTypes) {
    store.commit(mutationTypes.BASE, { type: mutationTypes.FAILURE, error: null, statusCode: null })
    store.commit(mutationTypes.BASE, { type: mutationTypes.SUCCESS, value: {} })
}

function setPendingFlag(store, mutationTypes, flag) {
    store.commit(mutationTypes.BASE, { type: mutationTypes.PENDING, value: flag })
}

export const doAsyncGet = (store, { url, mutationTypes, callback }) => {
    reset(store, mutationTypes)
    setPendingFlag(store, mutationTypes, true)

    return new Promise((resolve, reject) => {
        axios.get(url, { headers: getAuthHeader() })
            .then(response => {
                resolve(processResponse(store, mutationTypes, response, callback))
            })
            .catch(error => {
                reject(processError(store, mutationTypes, error))
            })
    })
}

export const doAsyncDelete = (store, { url, mutationTypes }) => {
    reset(store, mutationTypes)
    setPendingFlag(store, mutationTypes, true)

    return new Promise((resolve, reject) => {
        axios.delete(url, { headers: getAuthHeader() })
            .then(response => {
                resolve(processResponse(store, mutationTypes, response, null))
            })
            .catch(error => {
                reject(processError(store, mutationTypes, error))
            })
    })
}

export const doAsyncPost = (store, { url, mutationTypes, payload, callback }) => {
    reset(store, mutationTypes)
    setPendingFlag(store, mutationTypes, true)

    return new Promise((resolve, reject) => {
        axios.post(url, payload, { headers: getAuthHeader() })
            .then(response => {
                resolve(processResponse(store, mutationTypes, response, callback))
            })
            .catch(error => {
                reject(processError(store, mutationTypes, error))
            })
    })
}

export const doAsyncPut = (store, { url, mutationTypes, payload, callback }) => {
    reset(store, mutationTypes)
    setPendingFlag(store, mutationTypes, true)

    return new Promise((resolve, reject) => {
        axios.put(url, payload, { headers: getAuthHeader() })
            .then(response => {
                resolve(processResponse(store, mutationTypes, response, callback))
            })
            .catch(error => {
                reject(processError(store, mutationTypes, error))
            })
    })
}

export const getDownload = (store, { url, mutationTypes, payload, callback }) => {
    reset(store, mutationTypes)
    setPendingFlag(store, mutationTypes, true)

    return axios.put(url, payload.download, {
        responseType: 'blob',
        onDownloadProgress(progress) {
            payload.callback(payload.download.id, Math.round(progress.loaded / progress.total * 100))
        }
    })
        .then(response => {
            processResponse(store, mutationTypes, response, callback)
            const blob = new Blob([response.data])
            const filename = payload.download.filePath.replace(/^.*[\\\/]/, '')

            // IE doesn't allow using a blob object directly as link href
            // instead it is necessary to use msSaveOrOpenBlob
            if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                window.navigator.msSaveOrOpenBlob(blob, filename)
                return
            }

            // For other browsers:
            // Create a link pointing to the ObjectURL containing the blob.
            let link = document.createElement('a')
            const data = window.URL.createObjectURL(blob)
            link.href = data
            link.style.display = 'none'
            link.download = filename
            document.body.appendChild(link)
            link.click()

            setTimeout(function () {
                // For Firefox it is necessary to delay revoking the ObjectURL
                document.body.removeChild(link)
                window.URL.revokeObjectURL(data)
            }, 100)
        })
        .catch(error => {
            processError(store, mutationTypes, error)
        })
}
